import React, { useState, useRef, useEffect } from 'react';
import { BookOpen, FileText, X, ChevronLeft, ChevronRight, Loader2, Download, Columns, Square } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import HTMLFlipBook from 'react-pageflip';
import { useTheme } from '../context/ThemeContext';

// --- LOCAL WORKER SETUP (.mjs) ---
// pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

// pdfjs.GlobalWorkerOptions.workerSrc = new URL(
//   'pdfjs-dist/build/pdf.worker.min.mjs',
//   import.meta.url
// ).toString();

pdfjs.GlobalWorkerOptions.workerSrc =
  `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;


// --- FONT & CMAP CONFIGURATION ---
const pdfOptions = {
  cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
  cMapPacked: true,
  standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
};

// --- Mock Data ---
const MOCK_PUBLICATIONS = [
  {
    id: 1,
    title: "Indian Between the Seasons",
    type: "Magazine",
    date: "September 2026",
    coverImage: "https://images.unsplash.com/photo-1512820790803-83ca734da794?auto=format&fit=crop&w=600&q=80",
    pdfUrl: "/magazine1.pdf", 
    pages: 30
  }
];

// --- FLIPBOOK PAGE COMPONENT ---
const FlipPage = React.forwardRef(({ pageNumber }, ref) => {
  return (
    <div ref={ref} className="page-wrapper" style={{ backgroundColor: 'white', overflow: 'hidden', width: '400px', height: '560px', position: 'relative' }}>
      <Page 
        pageNumber={pageNumber} 
        width={400} 
        height={560}
        renderAnnotationLayer={false} 
        renderTextLayer={false}
        devicePixelRatio={window.devicePixelRatio || 1}
        loading={
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: '#9ca3af' }}>
            <Loader2 className="animate-spin" size={24} />
          </div>
        }
      />
    </div>
  );
});

const MagazinesPage = () => {
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState('All');
  const [selectedDoc, setSelectedDoc] = useState(null);
  
  // View Mode State: Default to single page on mobile, double page on desktop
  const [isSinglePage, setIsSinglePage] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsSinglePage(true);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // PDF Reader State
  const [numPages, setNumPages] = useState(null);
  const [isPdfLoading, setIsPdfLoading] = useState(true);
  const [pdfError, setPdfError] = useState(null);
  const flipBookRef = useRef(null);

  const filteredDocs = activeTab === 'All' 
    ? MOCK_PUBLICATIONS 
    : MOCK_PUBLICATIONS.filter(doc => doc.type === activeTab);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setIsPdfLoading(false);
    setPdfError(null);
  };

  const onDocumentLoadError = (error) => {
    console.error("PDF Load Error:", error);
    setIsPdfLoading(false);
    setPdfError(error.message);
  };

  const closeReader = () => {
    setSelectedDoc(null);
    setNumPages(null);
    setIsPdfLoading(true);
    setPdfError(null);
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: isDark ? '#111827' : '#f9fafb', paddingTop: '90px', paddingBottom: '60px', transition: 'background-color 0.3s', fontFamily: 'system-ui, sans-serif' }}>
      
      {/* HEADER SECTION */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px', textAlign: 'center', marginBottom: '40px' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '60px', height: '60px', backgroundColor: isDark ? '#064e3b' : '#dcfce7', borderRadius: '50%', marginBottom: '16px' }}>
          <BookOpen size={30} color="#16a34a" />
        </div>
        <h1 style={{ fontSize: 'clamp(32px, 5vw, 42px)', fontWeight: '900', color: isDark ? '#f9fafb' : '#111827', marginBottom: '12px' }}>
          Odessey Library
        </h1>
        <p style={{ fontSize: '16px', color: isDark ? '#9ca3af' : '#6b7280', maxWidth: '600px', margin: '0 auto 30px' }}>
          Flip through our exclusive travel magazines, curated guides, and monthly newsletters. 
        </p>

        {/* TABS */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
          {['All', 'Magazine', 'Newsletter'].map(tab => (
            <button 
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '8px 20px', borderRadius: '50px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.2s', border: 'none',
                backgroundColor: activeTab === tab ? '#16a34a' : (isDark ? '#1f2937' : 'white'),
                color: activeTab === tab ? 'white' : (isDark ? '#d1d5db' : '#4b5563'),
                boxShadow: activeTab === tab ? '0 4px 12px rgba(22, 163, 74, 0.3)' : (isDark ? 'none' : '0 2px 8px rgba(0,0,0,0.05)'),
                border: activeTab !== tab && isDark ? '1px solid #374151' : '1px solid transparent'
              }}
            >
              {tab === 'All' ? 'All Publications' : `${tab}s`}
            </button>
          ))}
        </div>
      </div>

      {/* GRID SECTION */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '30px' }}>
        {filteredDocs.map((doc) => (
          <div 
            key={doc.id}
            onClick={() => setSelectedDoc(doc)}
            className="magazine-card"
            style={{ 
              backgroundColor: isDark ? '#1f2937' : 'white', borderRadius: '20px', overflow: 'hidden', cursor: 'pointer', 
              border: isDark ? '1px solid #374151' : '1px solid #e5e7eb', boxShadow: isDark ? '0 10px 25px rgba(0,0,0,0.5)' : '0 10px 25px rgba(0,0,0,0.05)',
              transition: 'transform 0.3s, box-shadow 0.3s'
            }}
          >
            <div style={{ position: 'relative', height: '320px', overflow: 'hidden' }}>
              <img src={doc.coverImage} alt={doc.title} style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.5s' }} className="mag-cover" />
              <div style={{ position: 'absolute', top: '15px', right: '15px', backgroundColor: 'rgba(17, 24, 39, 0.8)', color: 'white', padding: '6px 12px', borderRadius: '50px', fontSize: '11px', fontWeight: 'bold', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                {doc.type === 'Magazine' ? <BookOpen size={12} /> : <FileText size={12} />} {doc.type}
              </div>
            </div>
            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '12px', color: '#16a34a', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '6px' }}>{doc.date}</p>
              <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#111827', marginBottom: '10px', lineHeight: '1.3' }}>{doc.title}</h3>
              <p style={{ fontSize: '13px', color: isDark ? '#9ca3af' : '#6b7280', display: 'flex', alignItems: 'center', gap: '4px' }}>
                {doc.pages} Pages
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* --- FLIPBOOK READER OVERLAY --- */}
      {selectedDoc && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 99999, backgroundColor: 'rgba(0,0,0,0.95)', backdropFilter: 'blur(10px)', display: 'flex', flexDirection: 'column', animation: 'fadeIn 0.3s ease-out' }}>
          
          {/* Reader Top Bar */}
          <div style={{ padding: '15px 30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'rgba(17, 24, 39, 0.8)', borderBottom: '1px solid #374151' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', color: 'white' }}>
              <BookOpen size={24} color="#16a34a" />
              <div>
                <h2 style={{ fontSize: '18px', fontWeight: 'bold', margin: 0 }}>{selectedDoc.title}</h2>
                <p style={{ fontSize: '12px', color: '#9ca3af', margin: 0 }}>{selectedDoc.date} • {isSinglePage ? 'Single Page View' : 'Two-Page Spread'}</p>
              </div>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              {/* View Mode Toggle Button */}
              <button 
                onClick={() => setIsSinglePage(!isSinglePage)} 
                style={{ color: 'white', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 'bold', backgroundColor: 'rgba(255,255,255,0.1)', border: 'none', padding: '8px 16px', borderRadius: '50px', cursor: 'pointer', transition: 'background 0.2s' }}
                title="Switch view mode"
              >
                {isSinglePage ? <Columns size={16} /> : <Square size={16} />} 
                <span>{isSinglePage ? 'Two-Page View' : 'Single View'}</span>
              </button>

              <a href={selectedDoc.pdfUrl} download style={{ color: 'white', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: 'bold', backgroundColor: 'rgba(255,255,255,0.1)', padding: '8px 16px', borderRadius: '50px', transition: 'background 0.2s' }} onMouseEnter={(e)=>e.currentTarget.style.backgroundColor='rgba(255,255,255,0.2)'} onMouseLeave={(e)=>e.currentTarget.style.backgroundColor='rgba(255,255,255,0.1)'}>
                <Download size={16} /> Download
              </a>
              <button onClick={closeReader} style={{ backgroundColor: '#ef4444', color: 'white', border: 'none', width: '40px', height: '40px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'transform 0.2s' }} onMouseEnter={(e)=>e.currentTarget.style.transform='scale(1.1)'} onMouseLeave={(e)=>e.currentTarget.style.transform='scale(1)'}>
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Reader Canvas Container */}
          <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', padding: '20px', overflow: 'hidden' }}>
            
            {isPdfLoading && !pdfError && (
              <div style={{ position: 'absolute', display: 'flex', flexDirection: 'column', alignItems: 'center', color: 'white', gap: '10px', zIndex: 10 }}>
                <Loader2 size={40} className="animate-spin" color="#16a34a" />
                <p style={{ fontWeight: 'bold' }}>Loading High-Quality Document...</p>
              </div>
            )}

            {/* Error State */}
            {pdfError && (
              <div style={{ backgroundColor: '#fee2e2', color: '#991b1b', padding: '20px', borderRadius: '12px', border: '1px solid #ef4444', textAlign: 'center', maxWidth: '400px' }}>
                <h3 style={{ fontWeight: 'bold', marginBottom: '8px' }}>Failed to load PDF</h3>
                <p style={{ fontSize: '14px' }}>We couldn't read the PDF file at <b>{selectedDoc.pdfUrl}</b>.</p>
                <p style={{ fontSize: '12px', marginTop: '10px', color: '#7f1d1d' }}>Error details: {pdfError}</p>
              </div>
            )}

            {!pdfError && (
              <Document
                file={selectedDoc.pdfUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                options={pdfOptions}
                loading={null}
              >
                {numPages && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    
                    {/* Flip Left Button */}
                    <button onClick={() => flipBookRef.current?.pageFlip()?.flipPrev()} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', width: '50px', height: '50px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s' }} onMouseEnter={(e)=>e.currentTarget.style.backgroundColor='rgba(255,255,255,0.3)'} onMouseLeave={(e)=>e.currentTarget.style.backgroundColor='rgba(255,255,255,0.1)'}>
                      <ChevronLeft size={28} />
                    </button>

                    {/* HTML Flipbook with Dynamic Single/Double Page Mode */}
                    <div style={{ boxShadow: '0 25px 50px -12px rgba(0,0,0,1)' }}>
                      <HTMLFlipBook
                        key={isSinglePage ? 'single' : 'double'} // Forces re-render on layout change
                        width={400}
                        height={560}
                        size="fixed" 
                        minWidth={315}
                        maxWidth={400}
                        minHeight={420}
                        maxHeight={560}
                        maxShadowOpacity={0.5}
                        showCover={!isSinglePage}
                        usePortrait={isSinglePage}
                        mobileScrollSupport={true}
                        ref={flipBookRef}
                        className="flipbook-container"
                      >
                        {Array.from(new Array(numPages), (el, index) => (
                          <FlipPage key={`page_${index + 1}`} pageNumber={index + 1} />
                        ))}
                      </HTMLFlipBook>
                    </div>

                    {/* Flip Right Button */}
                    <button onClick={() => flipBookRef.current?.pageFlip()?.flipNext()} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', width: '50px', height: '50px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s' }} onMouseEnter={(e)=>e.currentTarget.style.backgroundColor='rgba(255,255,255,0.3)'} onMouseLeave={(e)=>e.currentTarget.style.backgroundColor='rgba(255,255,255,0.1)'}>
                      <ChevronRight size={28} />
                    </button>

                  </div>
                )}
              </Document>
            )}

          </div>
        </div>
      )}

      <style>{`
        .magazine-card:hover .mag-cover { transform: scale(1.05); }
        .magazine-card:hover { transform: translateY(-5px); }
        
        .react-pdf__Page__canvas { 
          display: block !important;
          width: 400px !important; 
          height: 560px !important; 
        }

        .page-wrapper { background-color: white; border-right: 1px solid #e5e7eb; }
        .flipbook-container { border-radius: 4px; }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .animate-spin { animation: spin 1s linear infinite; }
        @keyframes spin { 100% { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default MagazinesPage;